import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Note } from 'app/models/dto/note.model';
import {
  AddNote,
  DeleteNote,
  ClearNoteStore ,
  GetBorrowerNotes,
  SubscribeToBorrowerNotes,
  UpdateNote,
} from 'app/store/notes/note.actions';
import { NotesService } from 'app/services/notes/notes.service';
import { catchError, tap } from 'rxjs/operators';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { Injectable } from '@angular/core';
import { differenceInSeconds } from 'date-fns';
import { of } from 'rxjs';
import { TeamDealsActions } from 'ng2-app/store/team-deals/team-deals.action';

export class NoteStateModel {
	notes: Note[];
  dupeBorrowerComponents: number;
}

@State<NoteStateModel>({
	name: 'notes',
	defaults: {
		notes: [],
    dupeBorrowerComponents: 0
	}
})

@Injectable()

export class NoteState {

  @Selector()
	static notes(state: NoteStateModel) {
    return state.notes;
  }

	constructor(
    private notesService: NotesService,
    private pusherService: PusherService,
    private store: Store,
  ) {}

  @Action(SubscribeToBorrowerNotes)
  subscribeToBorrowerNotes({ getState, patchState }: StateContext<NoteStateModel>, { borrowerId }: SubscribeToBorrowerNotes) {
    this.pusherSubscribe(borrowerId);
    const borrowerAlreadyLoaded = (getState().notes.findIndex(note => note.borrowerId === borrowerId) !== -1);
    if (borrowerAlreadyLoaded) {
      patchState({ dupeBorrowerComponents: getState().dupeBorrowerComponents + 1 });
    }
    this.store.dispatch(new GetBorrowerNotes(borrowerId));
  }

	@Action(GetBorrowerNotes)
	getBorrowerNotes({ getState, patchState }: StateContext<NoteStateModel>, { borrowerId }: GetBorrowerNotes) {
    const currentNotes = [ ...getState().notes.filter(note => note.borrowerId !== borrowerId) ];
    return this.notesService.getBorrowerNotes(borrowerId).pipe(
      catchError(() => of()),
      tap(notes => {
        const sortedNotes = notes.sort((left: Note, right: Note) => differenceInSeconds(new Date(right.created), new Date(left.created)));
        patchState({ notes: [ ...currentNotes, ...sortedNotes ]});
      }));
	}

	@Action(AddNote)
	addNote({ getState, patchState }: StateContext<NoteStateModel>, { note }: AddNote) {
    patchState({ notes: [ note, ...getState().notes ] });
    return this.notesService.save(note).pipe(
      tap(newNote => {
        const notesCopy = [ ...getState().notes];
        notesCopy[0] = newNote;
        patchState({ notes: notesCopy });
        this.store.dispatch( new TeamDealsActions.UpdateBorrowerNoteContent(note) );
		  }),
      catchError( err => {
        console.error(err);
        return of(err);
      })
    );
	}

	@Action(DeleteNote)
	deleteNote({ getState, patchState }: StateContext<NoteStateModel>, { noteId }: DeleteNote) {
		return this.notesService.delete(noteId).pipe(tap(() => {
			patchState({ notes: getState().notes.filter(note => note.id !== noteId) });
		}));
	}

	@Action(UpdateNote)
	updateNote({ getState, patchState }: StateContext<NoteStateModel>, { noteId, content }: UpdateNote) {
    const optimisticNotes = getState().notes.map(note => {
      if (note.id === noteId) {
          return { ...note, content };
      }
      return note;
    });
    patchState({ notes: optimisticNotes });
    return this.notesService.update(noteId, content).pipe(
      catchError(() => of()),
      tap(updatedNote => {
        const state = getState();
        const notes = [...state.notes];
        const updatedNoteIndex = notes.findIndex(note => note.id === noteId);
        notes[updatedNoteIndex] = updatedNote;
        patchState({ notes });
        this.store.dispatch( new TeamDealsActions.UpdateBorrowerNoteContent(updatedNote) );
      })
    );
	}

  @Action(ClearNoteStore)
  clearNoteStore({ getState, patchState }: StateContext<NoteStateModel>, { borrowerId }: ClearNoteStore) {
    if (getState().dupeBorrowerComponents === 0) {
      this.pusherUnsubscribe(borrowerId);
      patchState({ notes: getState().notes.filter(n => n.borrowerId !== borrowerId) });
    } else {
      patchState({ dupeBorrowerComponents: getState().dupeBorrowerComponents - 1 });
    }
  }

  pusherSubscribe(borrowerId: number) {
    const notesChannel = this.pusherService.subscribe(`borrower-${borrowerId}-notes`);
    notesChannel.bind_global((eventName: string) => {
      switch (eventName) {
        case 'note-updated':
        case 'note-deleted':
        case 'note-created':
          this.store.dispatch(new GetBorrowerNotes(borrowerId));
          break;
      }
    });
  }

  pusherUnsubscribe(borrowerId: number) {
    this.pusherService.unsubscribe(`borrower-${borrowerId}-notes`);
  }
}
